import type {
  NodeVariables,
  QueueRoutingPredicate,
  NodeVariableRoutingPredicate,
  DynamicVariableRoutingPredicate,
  QueueRoutingRule,
  NodeVariable,
} from 'src/types/chatbot';
import type {DynamicLabelExtra} from 'src/components/lib/variable-picker/types';
import type {EntityType} from 'src/types/ats-entities';
import produce from 'immer';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import xor from 'lodash/xor';
import debounce from 'lodash/debounce';
import {entries as objEntries} from 'src/utils/object';
import matchSorter from 'match-sorter';

import {Button as GenesisButton} from '@spaced-out/ui-design-system/lib/components/Button';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {
  SubTitleMedium,
  SubTitleExtraSmall,
  BodyLarge,
  TEXT_COLORS,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {
  Table,
  BasicSingleCell,
} from '@spaced-out/ui-design-system/lib/components/Table';

import {SearchInput} from 'src/designSystem2021Components/search-input/search-input';

import {ButtonDropdown} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';

import {useChatbotVariables} from '../node-editor/reducer';

import nodeCss from './lat.css';


export type TableVariable =
  | {
      __type: 'global_variable' | 'other' | 'ats_variable';
      __label: string;
      __value: string;
      __key: string;
      // and other stuff, but we only care about the above for the table and misc display;
    }
  | {
      __type: 'node_value';
      __label: string;
      __value: {
        node_id: number;
        node_name: string;
        derived_field_name?: string;
      };
      __key: string;
      // and other stuff, but we only care about the above for the table and misc display;
    };
function keyForVariable(v: NodeVariable | DynamicLabelExtra): string {
  // @ts-expect-error - TS2339 - Property 'node_id' does not exist on type 'NodeVariable | DynamicLabelExtra'. | TS2339 - Property 'node_name' does not exist on type 'NodeVariable | DynamicLabelExtra'.
  if (v.node_id != null && v.node_name) {
    // @ts-expect-error - TS2339 - Property 'node_id' does not exist on type 'NodeVariable | DynamicLabelExtra'. | TS2339 - Property 'derived_field_name' does not exist on type 'NodeVariable | DynamicLabelExtra'.
    return `${v.node_id}-${v.derived_field_name ?? ''}`;
  }
  // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'NodeVariable | DynamicLabelExtra'.
  if (!v.value) {
    throw new Error('cannot key an ats or custom variable without a .value');
  }
  // @ts-expect-error - TS2339 - Property 'value' does not exist on type 'NodeVariable | DynamicLabelExtra'.
  return v.value;
}

export function keyForPredicate(pred: QueueRoutingPredicate): string {
  return typeof pred.source_ref === 'string'
    ? pred.source_ref
    : `${pred.source_ref.node_id}-${pred.source_ref.derived_field_name ?? ''}`;
}

export function useAllVariables(
  nodeVariables: NodeVariables,
  entityType: EntityType,
  showExternalVariables: boolean = false,
): TableVariable[] {
  const dynamicLabels = useChatbotVariables(entityType, {
    showExternalVariables,
  });
  const tableEntries = React.useMemo(() => {
    const entries = [
      ...dynamicLabels
        // for now filter out custom variables (i.e. conversation length)
        // but can remove this if we ever include them again (i.e. browser, derived location, lang, etc)
        .filter((v) => v.source !== 'custom')
        .map((dl) => ({
          ...dl,
          __type:
            dl.source === 'global_variable'
              ? 'global_variable'
              : dl.source === 'custom'
              ? 'other'
              : 'ats_variable',
          __label: dl.label,
          __value: dl.value,
          __key: keyForVariable(dl),
        })),
      ...nodeVariables.map((nv) => ({
        ...nv,
        __type: 'node_value',
        __label: nv.name,
        __value: {
          node_id: nv.node_id,
          node_name: nv.node_name,
          derived_field_name: nv.derived_field_name,
        },
        __key: keyForVariable(nv),
      })),
    ];
    return entries;
  }, [dynamicLabels, nodeVariables]);

  return tableEntries;
}

const TK = () => {};

type VariableType = 'All' | 'Node' | 'ATS' | 'Other';
type VariableFilterOption = {
  key: string;
  label: VariableType;
};

export function RuleEditor({
  variables: tableEntries,
  rule,
  onAddPredicates,
}: {
  rule: QueueRoutingRule;
  variables: TableVariable[];
  onAddPredicates: (predicates: QueueRoutingPredicate[]) => unknown;
}) {
  const {t, makeKey} = useLabelTranslation();
  const [predicates, setPredicates] = React.useState(rule.predicates);

  const [variableFilterType, setVariableFilterType] =
    React.useState<VariableType>('All');
  const [searchFilterValue, setSearchFilterValue] = React.useState('');

  const filteredValues = React.useMemo(() => {
    // i'm not sure it's worth making a lookup for this.
    if (variableFilterType === 'Other') {
      return tableEntries.filter((entry) => entry.__type === 'other');
    }
    if (variableFilterType === 'Node') {
      return tableEntries.filter((entry) => entry.__type === 'node_value');
    }
    if (variableFilterType === 'ATS') {
      return tableEntries.filter((entry) => entry.__type === 'ats_variable');
    }
    return tableEntries;
  }, [variableFilterType, tableEntries]);

  // @ts-expect-error - TS2322 - Type '{ key: string; label: string; }[]' is not assignable to type 'VariableFilterOption[]'.
  const variableFilterLabels: VariableFilterOption[] = [
    'All',
    'Node',
    'ATS',
    'Other',
  ].map((label, idx) => ({
    key: `${idx}`,
    // $FlowFixMe[incompatible-type]
    label: t(makeKey(label), label),
  }));

  const filteredEntries = matchSorter(filteredValues, searchFilterValue, {
    keys: ['__label'],
  });

  const newPredicateWithKey = (key: string): QueueRoutingPredicate => {
    const matchingVariable: TableVariable | null | undefined =
      tableEntries.find((e) => e.__key === key);
    if (!matchingVariable) {
      // throw new Error(`could not find a matching variable for key ${key}`);
      console.error(`could not find a matching variable for key ${key}`);
      return {
        pred_type: 'equal',
        source_type: 'other',
        source_ref: key,
        value: '',
      };
    }
    if (matchingVariable.__type === 'node_value') {
      const nvRoutingPred: NodeVariableRoutingPredicate = {
        pred_type: 'equal',
        source_type: 'node_value',
        source_ref: matchingVariable.__value,
        value: '',
      };
      return nvRoutingPred;
    } else {
      if (
        (matchingVariable.__type === 'ats_variable' ||
          matchingVariable.__type === 'global_variable') &&
        typeof matchingVariable.__value === 'string'
      ) {
        const dvRoutingPred: DynamicVariableRoutingPredicate = {
          pred_type: 'equal',
          source_type: matchingVariable.__type,
          source_ref: matchingVariable.__value,
          value: '',
        };
        return dvRoutingPred;
      }
    }
    return {
      pred_type: 'equal',
      source_type: 'other',
      source_ref: key,
      value: '',
    };
  };

  const onChangePredicateValue = (
    key: string,
    nextValue: string | string[],
    // @ts-expect-error - TS2355 - A function whose declared type is neither 'undefined', 'void', nor 'any' must return a value.
  ): unknown => {
    const nextPredicates = produce(predicates, (draft) => {
      const idx = predicates.findIndex((pred) => keyForPredicate(pred) === key);
      if (idx > -1) {
        draft[idx].value = nextValue;
      } else {
        draft.push({...newPredicateWithKey(key), value: nextValue});
      }
    });
    setPredicates(nextPredicates);
  };

  const onChangePredicateOperator = (
    key: string,
    nextOperator: 'equal' | 'not_equal' | 'in' | 'not_in',
    // @ts-expect-error - TS2355 - A function whose declared type is neither 'undefined', 'void', nor 'any' must return a value.
  ): unknown => {
    const nextPredicates = produce(predicates, (draft) => {
      const idx = predicates.findIndex((pred) => keyForPredicate(pred) === key);
      if (idx > -1) {
        draft[idx].pred_type = nextOperator;
        if (['in', 'not_in'].includes(nextOperator)) {
          if (!Array.isArray(draft[idx].value)) {
            draft[idx].value = draft[idx].value.split(',');
          }
        } else {
          if (Array.isArray(draft[idx].value)) {
            draft[idx].value = draft[idx].value.join(', ');
          }
        }
      } else {
        draft.push({...newPredicateWithKey(key), pred_type: nextOperator});
      }
    });
    setPredicates(nextPredicates);
  };

  return (
    <div className={nodeCss.ruleEditor}>
      <div className={nodeCss.editorTopActions}>
        <SubTitleMedium>
          {t('Create_Condition', 'Create Condition')}
        </SubTitleMedium>
        <GenesisButton
          disabled={predicates.length === 0}
          onClick={() => {
            // TODO(AMAN): Hacky solution, get rid of this once token list input is used here
            const mappedPredicates = predicates.map(
              (predicate: QueueRoutingPredicate) => {
                const _predicate = {...predicate} as const;

                if (
                  _predicate.pred_type === 'in' ||
                  _predicate.pred_type === 'not_in'
                ) {
                  if (typeof _predicate.value === 'string') {
                    // @ts-expect-error - TS2540 - Cannot assign to 'value' because it is a read-only property.
                    _predicate.value = _predicate.value.split(',');
                  }
                  // @ts-expect-error - TS2540 - Cannot assign to 'value' because it is a read-only property.
                  _predicate.value = _predicate.value
                    .map((str) => str.trim())
                    .filter((str) => str !== '');
                }
                return _predicate;
              },
            );
            onAddPredicates(mappedPredicates);
          }}
        >
          {t('Add', 'Add')}
        </GenesisButton>
      </div>
      <div className={nodeCss.searchRow}>
        <SearchInput
          isDebounce
          onChange={setSearchFilterValue}
          value={searchFilterValue}
        />

        <SubTitleExtraSmall
          // @ts-expect-error - TS2769 - No overload matches this call.
          value={searchFilterValue}
          color="secondary"
          className={nodeCss.filterLabel}
        >
          {t('Filter_Variables', 'Filter Variables')}
        </SubTitleExtraSmall>
        <ButtonDropdown
          menu={{
            options: variableFilterLabels,
          }}
          // @ts-expect-error - TS2769 - No overload matches this call.
          onOptionSelect={(opt: VariableFilterOption) => {
            // FlowIssue[incompatible-call]
            if (!opt.label) {
              throw new Error('cannot set <void> filter type');
            }
            setVariableFilterType(opt.label);
          }}
          size="small"
        >
          {t(makeKey(variableFilterType), variableFilterType)}
        </ButtonDropdown>
      </div>
      <div className={nodeCss.paddedTable}>
        <Table
          key={variableFilterType}
          entries={filteredEntries}
          emptyText={
            <BodyLarge
              color={TEXT_COLORS.secondary}
              className={nodeCss.defaultEmptyText}
            >{`${t(
              'Nothing_to_display_here',
              'Nothing to display here',
            )}.`}</BodyLarge>
          }
          selectedKeys={predicates.map(keyForPredicate)}
          onSelect={(keys) => {
            let nextPredicates;
            if (keys.length > predicates.length) {
              const keysToAdd = xor(keys, predicates.map(keyForPredicate));
              nextPredicates = [
                ...predicates,
                ...keysToAdd.map(newPredicateWithKey),
              ];
            } else {
              const keysToRemove = xor(keys, predicates.map(keyForPredicate));
              nextPredicates = predicates.filter(
                (p) => !keysToRemove.includes(keyForPredicate(p)),
              );
            }
            setPredicates(nextPredicates);
          }}
          idName="__key"
          extras={{
            onChangePredicateOperator,
            onChangePredicateValue,
            predicates,
          }}
          headers={[
            {
              label: t('Variable', 'Variable'),
              key: '__label',
              className: nodeCss.variableLabelColumn,
            },
            {
              label: t('Criteria', 'Criteria'),
              // @ts-expect-error - TS2322 - Type '"operator"' is not assignable to type '"__label" | "__type" | "__value" | "__key"'.
              key: 'operator',
              render: OperatorDropdown,
            },
            // @ts-expect-error - TS2322 - Type '"val"' is not assignable to type '"__label" | "__type" | "__value" | "__key"'.
            {label: t('Values', 'Values'), key: 'val', render: PredicateValue},
          ]}
        />
      </div>
    </div>
  );
}

type TableCellExtras = {
  predicates: QueueRoutingPredicate[];
  onChangePredicateOperator: (
    key: string,
    op: 'equal' | 'not_equal' | 'in' | 'not_in',
  ) => unknown;
  onChangePredicateValue: (key: string, value: string | string[]) => unknown;
};

export const PRED_TYPE_LABEL_MAP = (
  t: (key: string, value: string) => string,
): {
  [key: string]: string;
} => ({
  equal: t('Is', 'Is'),
  not_equal: t('Is_Not', 'Is Not'),
  in: t('One_Of', 'One Of'),
  not_in: t('Not_One_Of', 'Not One Of'),
});

function OperatorDropdown({
  data,
  extras,
}: {
  className?: string;
  selected?: boolean;
  data: TableVariable;
  extras?: TableCellExtras;
  disabled?: boolean;
}) {
  const {t, makeKey} = useLabelTranslation();
  // weird guard because extras are typed as optional
  const {predicates, onChangePredicateOperator} = extras ?? {};
  // @ts-expect-error - TS18048 - 'predicates' is possibly 'undefined'.
  const currentPredicate = predicates.find(
    (p) => keyForPredicate(p) === data.__key,
  );
  return (
    <BasicSingleCell>
      <ButtonDropdown
        menu={{
          options: objEntries<string, string>(PRED_TYPE_LABEL_MAP(t)).map(
            ([k, v]: [any, any]) => ({
              key: k,
              label: v,
            }),
          ),
        }}
        onOptionSelect={(opt) => {
          // @ts-expect-error - TS2722 - Cannot invoke an object which is possibly 'undefined'. | TS2345 - Argument of type 'string' is not assignable to parameter of type '"in" | "not_in" | "equal" | "not_equal"'.
          onChangePredicateOperator(data.__key, opt.key);
        }}
        type="secondary"
        size="small"
      >
        {PRED_TYPE_LABEL_MAP(t)[currentPredicate?.pred_type ?? 'equal']}
      </ButtonDropdown>
    </BasicSingleCell>
  );
}

function PredicateValue({
  data,
  extras,
}: {
  className?: string;
  selected?: boolean;
  data: TableVariable;
  extras?: TableCellExtras;
  disabled?: boolean;
}) {
  const {predicates, onChangePredicateValue} = extras ?? {};
  // @ts-expect-error - TS18048 - 'predicates' is possibly 'undefined'.
  const currentPredicate = predicates.find(
    (p) => keyForPredicate(p) === data.__key,
  );
  const currentValue = currentPredicate?.value ?? '';

  // because setting predicate values causes the whole table to rerender
  // (i know, i know...), we control the component value and debounce the
  // predicate onchange here
  const [localValue, setLocalValue] = React.useState<string[] | string>(
    currentValue,
  );
  // @ts-expect-error - TS2345 - Argument of type '((key: string, value: string | string[]) => unknown) | undefined' is not assignable to parameter of type '(...args: any) => any'.
  const debouncePredicateChange = debounce(onChangePredicateValue, 100);

  const onChange = (value: string | Array<string>, flush = false) => {
    setLocalValue(value);
    debouncePredicateChange(data.__key, value);
  };

  // sorry this is very hacky until i get token list input back
  if (
    Array.isArray(localValue) ||
    currentPredicate?.pred_type === 'in' ||
    currentPredicate?.pred_type === 'not_in'
  ) {
    return (
      <BasicSingleCell>
        <Input
          value={Array.isArray(localValue) ? localValue.join(',') : localValue}
          onChange={(evt) => {
            const vals = evt.currentTarget.value.split(',');
            onChange(vals);
          }}
          onBlur={(evt) => {
            const vals = evt.currentTarget.value
              .split(',')
              .map((str) => str.trim())
              .filter((str) => str !== '');
            onChange(vals);
          }}
        />
      </BasicSingleCell>
    );
  }

  return (
    <BasicSingleCell>
      <Input
        value={localValue}
        onChange={(evt) => {
          onChange(evt.currentTarget.value);
        }}
      />
    </BasicSingleCell>
  );
}
